import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ScrollText } from "lucide-react";

const API = `${window.location.origin}/api`;

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return "-"; }
}

export default function LogsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all"); // "all", "found", "notfound"
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filter === "found") params.found = true;
      if (filter === "notfound") params.found = false;
      const res = await axios.get(`${API}/logs`, { headers, params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error("Loglar yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [page, filter]); // eslint-disable-line

  const totalPages = Math.ceil(total / LIMIT);

  const filterBtns = [
    { key: "all", label: "Tümü" },
    { key: "found", label: "Bulundu" },
    { key: "notfound", label: "Bulunamadı" },
  ];

  return (
    <Layout>
      <div className="p-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              API Logları
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {total.toLocaleString("tr-TR")} toplam istek
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-md">
            {filterBtns.map(({ key, label }) => (
              <button
                key={key}
                data-testid={`filter-${key}-btn`}
                onClick={() => { setFilter(key); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-bold uppercase rounded-md transition-all ${
                  filter === key
                    ? "bg-white text-gray-800 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <ScrollText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Henüz log kaydı yok</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="logs-table">
                  <thead>
                    <tr className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-3 text-left">Tarih / Saat</th>
                      <th className="px-4 py-3 text-left">Model Metni</th>
                      <th className="px-4 py-3 text-left">Marka</th>
                      <th className="px-4 py-3 text-left">Kanal</th>
                      <th className="px-4 py-3 text-left">Sonuç</th>
                      <th className="px-4 py-3 text-left">Bulunan Model</th>
                      <th className="px-4 py-3 text-left">API Anahtarı</th>
                      <th className="px-4 py-3 text-right">Yanıt (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">
                          {formatDt(log.created_at)}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">{log.model_text}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{log.brand || "-"}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{log.channel || "-"}</td>
                        <td className="px-4 py-3">
                          {log.found ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-sm">
                              <CheckCircle2 className="w-3 h-3" /> Bulundu
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-sm">
                              <XCircle className="w-3 h-3" /> Bulunamadı
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 font-medium">{log.result_model || "-"}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                            {log.api_key_name || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-gray-500">
                          {log.response_time_ms}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} / {total}
                  </p>
                  <div className="flex items-center gap-1">
                    <button data-testid="logs-prev-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50">
                      Önceki
                    </button>
                    <span className="px-3 py-1.5 text-xs text-gray-600">{page} / {totalPages}</span>
                    <button data-testid="logs-next-btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50">
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
